/**
 * Glossary text-wrapping helper.
 *
 * Given a reply string, a list of flagged glossary keys, and the program
 * config + active language, return a list of {text, termKey?} segments so
 * a React renderer can wrap flagged surface forms with <GlossaryTerm>.
 *
 * Surface form lookup: for a glossary term with `in_chat: USE`, match the
 * localized `term`. For `REPHRASE`, match the localized `rephrase_as`.
 * Matches are case-insensitive but preserve the original case in the
 * rendered output. Terms not found in the reply are quietly dropped — the
 * Worker flagged them but the literal text isn't present (LLM paraphrased
 * differently than the config anticipated).
 */

import type { ProgramConfig } from '../config/types';
import type { Language } from '../api/types';

export interface TextSegment {
  text: string;
  /** Set when this segment is a flagged glossary term. */
  termKey?: string;
}

/** Escape a string so it can be used inside a RegExp literal. */
function escapeRe(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Pull the surface text to match for a given glossary key. */
function surfaceFor(
  config: ProgramConfig,
  language: Language,
  termKey: string,
): string | null {
  const entry = config.glossary[termKey];
  if (!entry) return null;
  if (entry.in_chat === 'REPHRASE' && entry.rephrase_as) {
    return entry.rephrase_as[language] ?? null;
  }
  return entry.term[language] ?? null;
}

/** Split `text` into segments, wrapping the first occurrence of each term
 *  key's surface form. Subsequent occurrences stay as plain text so a single
 *  paragraph doesn't get peppered with underlines. */
export function segmentReply(
  text: string,
  termKeys: readonly string[],
  config: ProgramConfig,
  language: Language,
): TextSegment[] {
  if (termKeys.length === 0 || text.length === 0) {
    return [{ text }];
  }

  // Build a list of (termKey, surface) pairs in order; longest first so a
  // multi-word term like "Rapid Response" beats its substrings.
  const candidates: Array<{ termKey: string; surface: string }> = [];
  for (const key of termKeys) {
    const surface = surfaceFor(config, language, key);
    if (surface && surface.trim().length > 0) {
      candidates.push({ termKey: key, surface });
    }
  }
  candidates.sort((a, b) => b.surface.length - a.surface.length);

  // Track the first-hit index for each candidate to avoid double-wrapping.
  const used = new Set<string>();
  const segments: TextSegment[] = [];
  let cursor = 0;

  while (cursor < text.length) {
    let best: { index: number; surface: string; termKey: string } | null = null;
    for (const { termKey, surface } of candidates) {
      if (used.has(termKey)) continue;
      const re = new RegExp(escapeRe(surface), 'i');
      const hit = re.exec(text.slice(cursor));
      if (hit && hit.index >= 0) {
        const abs = cursor + hit.index;
        if (best === null || abs < best.index) {
          best = { index: abs, surface: hit[0], termKey };
        }
      }
    }

    if (best === null) {
      segments.push({ text: text.slice(cursor) });
      break;
    }

    if (best.index > cursor) {
      segments.push({ text: text.slice(cursor, best.index) });
    }
    segments.push({ text: best.surface, termKey: best.termKey });
    used.add(best.termKey);
    cursor = best.index + best.surface.length;
  }

  return segments;
}
